import { useEffect, useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { openCustomerPortal, startCheckout } from "../../lib/stripe";
import PromoBanner from "../PromoBanner";
import { getDiscount, BASE_PRICE, discountedPrice, savedAmount } from "../../lib/promo";
import { useAuth } from "../AuthContext";
import { C } from "../../lib/colors";
import { useT } from "../LangContext";

// ─── Fase 17B · Bloque 3 — Account view v2 ─────────────────────────────
// Three sections: 1) "Tu cuenta" (email, plan, status, renewal),
// 2) "Planes" (three cards Free/Pro/Annual with CTAs), 3) "Gestionar
// suscripción" (Stripe portal — the ONLY entry-point in the app) + logout.
// The header user icon now drives the navigation here; a "← volver" link
// returns to the previously-active tab.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(unix, lang) {
  if (!unix) return null;
  try {
    const d = new Date(unix * 1000);
    return d.toLocaleDateString(lang === "es" ? "es-ES" : "en-US",
      { year: "numeric", month: "short", day: "numeric" });
  } catch { return null; }
}

function parseFeatures(raw) {
  try { return JSON.parse(raw); } catch { return []; }
}

export default function AccountView({ setActiveTab, previousTab }) {
  const { t, lang } = useT();
  const { user, signOut } = useAuth();
  const [sub, setSub] = useState("loading"); // "loading" | {...} | "error"
  const [portalLoading, setPortalLoading] = useState(false);

  const load = useCallback(async () => {
    setSub("loading");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) { setSub("error"); return; }
      const res = await fetch(`${BACKEND_URL}/api/user/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setSub("error"); return; }
      setSub(await res.json());
    } catch {
      setSub("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const goBack = () => {
    const target = previousTab && previousTab !== "account" ? previousTab : "chamber";
    if (setActiveTab) setActiveTab(target);
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try { await openCustomerPortal(); }
    finally { setPortalLoading(false); }
  };

  return (
    <div className="pf-tab-body fg-fadein" data-testid="pane-account">
      <div style={{ display: "flex", alignItems: "center",
                     justifyContent: "space-between", gap: 12,
                     marginBottom: 18, flexWrap: "wrap" }}>
        <button onClick={goBack} data-testid="account-back-btn"
                style={{ background: "transparent", border: "none", color: C.smoke,
                          fontFamily: "var(--mono)", fontSize: 12,
                          letterSpacing: 0.18, cursor: "pointer", padding: 0,
                          textDecoration: "underline dotted" }}>
          {t("account_back")}
        </button>
        <div className="pf-tab-kicker">06 · {t("tab_account")}</div>
      </div>

      <div style={{ maxWidth: 920, margin: "0 auto" }}>
        {sub === "loading" && (
          <div data-testid="account-loading"
               style={{ color: C.smoke, fontFamily: "var(--mono)",
                         fontSize: 12, letterSpacing: 0.18, padding: 24 }}>
            {t("account_loading")}
          </div>
        )}

        {sub === "error" && (
          <div data-testid="account-error"
               style={{ padding: 16, border: "1px solid #C15733",
                         background: "rgba(193, 87, 51, 0.08)", color: "#C15733",
                         fontFamily: "var(--mono)", fontSize: 12 }}>
            ⚠ {t("account_error")}
            <button onClick={load} data-testid="account-retry"
                    style={{ marginLeft: 12, background: "transparent",
                              border: "1px solid #C15733", color: "#C15733",
                              padding: "4px 10px", cursor: "pointer",
                              fontFamily: "var(--mono)", fontSize: 11 }}>
              ↻ {t("account_retry")}
            </button>
          </div>
        )}

        {sub && typeof sub === "object" && (
          <>
            <YourAccountSection sub={sub} user={user} t={t} lang={lang} />
            <PlansSection sub={sub} t={t} />
            <ManageSection sub={sub} t={t} onPortal={openPortal}
                            portalLoading={portalLoading}
                            onLogout={() => signOut()} />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Section 1: Your account ────────────────────────────────────────────
function YourAccountSection({ sub, user, t, lang }) {
  const planLabel = t(`account_plan_${sub.plan}`) || sub.plan;
  const statusLabel = t(`account_status_${sub.status}`) || sub.status;
  const renewal = formatDate(sub.renewal_at, lang);
  const renewalLine = renewal && (
    sub.cancel_at_period_end
      ? t("account_cancels_on", { date: renewal })
      : t("account_renews_on", { date: renewal })
  );

  return (
    <section data-testid="account-yours" style={{ marginBottom: 32 }}>
      <SectionHeader text={t("account_section_yours")} />
      <div style={{ padding: 22, border: `1px solid ${C.dust}`,
                     background: "rgba(42, 48, 50, 0.30)" }}>
        {user?.email && (
          <Row label="Email" value={
            <span data-testid="account-email" style={{ color: C.linen,
                                                          fontFamily: "var(--mono)",
                                                          fontSize: 12.5 }}>
              {user.email}
            </span>
          } />
        )}
        <Row label={t("account_plan_label")} value={
          <span data-testid="account-plan" style={{ color: C.brass, fontWeight: 500 }}>
            {planLabel}
          </span>
        } />
        <Row label={t("account_status_label")} value={
          <span data-testid="account-status"
                style={{ color: sub.status === "active" ? C.bone : C.smoke }}>
            {statusLabel}
          </span>
        } />
        {renewalLine && (
          <Row label={t("account_renewal_label")} value={
            <span data-testid="account-renewal" style={{ color: C.linen }}>
              {renewalLine}
            </span>
          } />
        )}
      </div>
    </section>
  );
}

// ─── Section 2: Plans ───────────────────────────────────────────────────
function PlansSection({ sub, t }) {
  const current = sub.plan || "free";
  const plans = ["free", "pro", "annual"];

  const handleUpgrade = (target) => {
    // El backend espera 'pro_monthly' | 'annual'. Las tarjetas usan la clave
    // de plan ('pro' | 'annual'), así que traducimos 'pro' → 'pro_monthly'.
    const planType = target === "pro" ? "pro_monthly" : target;
    startCheckout(planType);
  };

  return (
    <section data-testid="account-plans" style={{ marginBottom: 32 }}>
      <SectionHeader text={t("account_plans_title")} />
      <PromoBanner compact />
      <div style={{ display: "grid",
                     gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                     gap: 14 }}>
        {plans.map(p => (
          <PlanCard key={p} planKey={p}
                    isCurrent={current === p}
                    t={t}
                    onUpgrade={handleUpgrade}
                    currentPlan={current} />
        ))}
      </div>
      <p data-testid="account-downgrade-hint"
         style={{ marginTop: 14, color: C.smoke,
                   fontFamily: "var(--plex)", fontSize: 12,
                   fontStyle: "italic" }}>
        {t("account_downgrade_hint")}
      </p>
    </section>
  );
}

function PlanCard({ planKey, isCurrent, currentPlan, t, onUpgrade }) {
  const { lang } = useT();
  const locale = lang === "es" ? "es-ES" : "en-US";
  const base = BASE_PRICE[planKey];
  const features = parseFeatures(t(`account_plan_${planKey}_features`));
  const price = t(`account_plan_${planKey}_price`);
  const label = t(`account_plan_${planKey}`);

  let cta = null;
  if (!isCurrent) {
    if (planKey === "free") {
      // Can't downgrade to free via UI per spec
      cta = null;
    } else if (currentPlan === "free") {
      cta = planKey === "pro" ? t("account_pro_cta") : t("account_annual_cta");
    } else if (currentPlan === "pro" && planKey === "annual") {
      cta = t("account_switch_annual");
    }
  }

  const borderColor = isCurrent ? C.brass : C.dust;
  const accent = isCurrent ? C.brass : C.smoke;

  return (
    <div data-testid={`plan-card-${planKey}`}
         data-current={isCurrent}
         style={{ padding: 18, border: `1px solid ${borderColor}`,
                   background: isCurrent ? "rgba(184, 164, 120, 0.06)"
                                          : "rgba(42, 48, 50, 0.22)",
                   display: "flex", flexDirection: "column", gap: 10,
                   minHeight: 220 }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                     alignItems: "baseline" }}>
        <div style={{ color: accent, fontFamily: "var(--mono)",
                       fontSize: 11, letterSpacing: 0.22,
                       textTransform: "uppercase" }}>
          {label}
        </div>
        {isCurrent && (
          <span data-testid={`plan-card-${planKey}-current-badge`}
                style={{ padding: "2px 8px", border: `1px solid ${C.brass}`,
                          color: C.brass, fontFamily: "var(--mono)",
                          fontSize: 9.5, letterSpacing: 0.18,
                          textTransform: "uppercase" }}>
            {t("account_current_badge")}
          </span>
        )}
      </div>
      <div>
        {getDiscount().active && base && (
          <div style={{ fontFamily: "var(--mono)", fontSize: 12, color: C.smoke,
                         textDecoration: "line-through", textDecorationColor: C.cinnabar }}>
            {price}
          </div>
        )}
        <div style={{ fontFamily: "var(--serif), 'Fraunces', serif",
                       fontSize: 22, color: C.bone, fontWeight: 400 }}>
          {getDiscount().active && base ? price.replace(String(base), discountedPrice(base, locale)) : price}
        </div>
        {getDiscount().active && base && (
          <div style={{ fontFamily: "var(--mono)", fontSize: 10.5, color: C.verdigris, marginTop: 3 }}>
            {t("promo_save", { amt: `${savedAmount(base, locale)}€` })} · {planKey === "annual" ? t("promo_first_year") : t("promo_first_month")}
          </div>
        )}
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0,
                    fontFamily: "var(--plex)", fontSize: 12.5,
                    color: C.linen, lineHeight: 1.55, flex: 1 }}>
        {features.map((f, i) => (
          <li key={i} style={{ paddingLeft: 14, position: "relative",
                                marginBottom: 4 }}>
            <span style={{ position: "absolute", left: 0,
                            color: C.brass }}>·</span> {f}
          </li>
        ))}
      </ul>
      {cta && (
        <button data-testid={`plan-card-${planKey}-cta`}
                onClick={() => onUpgrade(planKey)}
                style={{ background: "transparent",
                          border: `1px solid ${C.brass}`, color: C.brass,
                          padding: "8px 14px", cursor: "pointer",
                          fontFamily: "var(--mono)", fontSize: 11,
                          letterSpacing: 0.18, marginTop: 4 }}>
          {cta}
        </button>
      )}
    </div>
  );
}

// ─── Section 3: Manage subscription + logout ───────────────────────────
function ManageSection({ sub, t, onPortal, portalLoading, onLogout }) {
  const showPortal = sub.has_stripe_customer && sub.plan !== "free";

  return (
    <section data-testid="account-manage" style={{ marginBottom: 16 }}>
      <SectionHeader text={t("account_section_manage")} />
      <div style={{ padding: 22, border: `1px solid ${C.dust}`,
                     background: "rgba(42, 48, 50, 0.20)",
                     display: "flex", flexDirection: "column", gap: 14 }}>
        {showPortal ? (
          <>
            <div style={{ color: C.smoke, fontFamily: "var(--plex)",
                            fontSize: 12.5, lineHeight: 1.55 }}>
              {t("account_section_manage_hint")}
            </div>
            <div>
              <button onClick={onPortal} disabled={portalLoading}
                      data-testid="account-portal-btn"
                      style={{ background: "transparent",
                                border: `1px solid ${portalLoading ? C.dust : C.brass}`,
                                color: portalLoading ? C.smoke : C.brass,
                                padding: "10px 20px",
                                cursor: portalLoading ? "wait" : "pointer",
                                fontFamily: "var(--mono)", fontSize: 12,
                                letterSpacing: 0.18,
                                opacity: portalLoading ? 0.6 : 1 }}>
                {portalLoading ? t("account_portal_loading")
                              : `→ ${t("account_manage_btn")}`}
              </button>
            </div>
          </>
        ) : (
          <div data-testid="account-no-sub-body"
               style={{ color: C.smoke, fontFamily: "var(--plex)",
                         fontSize: 12.5, lineHeight: 1.55 }}>
            {t("account_no_sub_body")}
          </div>
        )}
        <div style={{ borderTop: `1px dashed ${C.dust}`, paddingTop: 12 }}>
          <button onClick={onLogout} data-testid="account-logout-btn"
                  style={{ background: "transparent",
                            border: `1px solid ${C.dust}`, color: C.smoke,
                            padding: "8px 18px", cursor: "pointer",
                            fontFamily: "var(--mono)", fontSize: 11,
                            letterSpacing: 0.18 }}>
            {t("account_logout")}
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── shared bits ────────────────────────────────────────────────────────
function SectionHeader({ text }) {
  return (
    <div style={{ color: C.brass, fontFamily: "var(--mono)", fontSize: 10.5,
                   letterSpacing: 0.25, textTransform: "uppercase",
                   marginBottom: 10 }}>
      {text}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between",
                   alignItems: "center", padding: "10px 0",
                   borderBottom: `1px solid ${C.dust}55` }}>
      <span style={{ color: C.smoke, fontFamily: "var(--mono)",
                      fontSize: 11, letterSpacing: 0.18,
                      textTransform: "uppercase" }}>{label}</span>
      <span style={{ fontFamily: "var(--plex)", fontSize: 14 }}>{value}</span>
    </div>
  );
}
